import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { Monitor, Smartphone, Tablet, ExternalLink, RefreshCw } from "lucide-react";

/**
 * Sticky live-preview shell used inside admin editor dialogs.
 *
 * Renders children inside a scaled-down "mini browser" frame so admins can
 * see edits in real time without leaving the editor.
 *
 * The inner content is rendered at a natural device width (390 / 820 / 1280)
 * and then visually scaled to fit the available pane width — the wrapper
 * height is dynamically measured so the outer scrollbar reflects the
 * *scaled* height of the content (not the raw pre-scale height).
 */
const VIEWPORTS = {
  desktop: { width: 1280, label: "Desktop", icon: Monitor },
  tablet:  { width: 820,  label: "Tablet",  icon: Tablet },
  mobile:  { width: 390,  label: "Mobile",  icon: Smartphone },
};

const LivePreviewPane = ({
  title = "Live preview",
  viewport: initialVp = "desktop",
  openUrl,
  onRefresh,
  children,
  testId = "live-preview-pane",
}) => {
  const [vp, setVp] = useState(initialVp);
  const [paneWidth, setPaneWidth] = useState(0);
  const [childHeight, setChildHeight] = useState(600);
  const paneRef = useRef(null);
  const childRef = useRef(null);

  const cfg = VIEWPORTS[vp];

  // Track the actual width of the preview pane
  useLayoutEffect(() => {
    if (!paneRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setPaneWidth(e.contentRect.width);
    });
    ro.observe(paneRef.current);
    return () => ro.disconnect();
  }, []);

  // Track natural height of the rendered preview so we can size the
  // outer scrollable area to match the scaled height.
  useLayoutEffect(() => {
    if (!childRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setChildHeight(e.contentRect.height || 600);
    });
    ro.observe(childRef.current);
    return () => ro.disconnect();
  }, [children]);

  // Scale = available width / target viewport width. Capped at 1 (never upscale).
  const scale = paneWidth > 0 ? Math.min(1, paneWidth / cfg.width) : 0.5;
  const scaledHeight = childHeight * scale;

  return (
    <div
      className="rounded-2xl border border-white/10 bg-slate-950/70 backdrop-blur-xl flex flex-col h-full min-h-[480px]"
      data-testid={testId}
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-rose-500/60" />
          <span className="h-2 w-2 rounded-full bg-amber-500/60" />
          <span className="h-2 w-2 rounded-full bg-emerald-500/60" />
          <div className="ml-3 text-[10px] uppercase tracking-[0.2em] text-slate-400">{title}</div>
        </div>
        <div className="flex items-center gap-1">
          {Object.entries(VIEWPORTS).map(([key, v]) => {
            const Icon = v.icon;
            const active = vp === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setVp(key)}
                className={`p-1.5 rounded-md transition ${
                  active ? "bg-atc-blue/20 text-atc-blue" : "text-slate-400 hover:text-white"
                }`}
                title={v.label}
                aria-label={`Preview as ${v.label}`}
                data-testid={`preview-vp-${key}`}
              >
                <Icon className="h-3.5 w-3.5" />
              </button>
            );
          })}
          {onRefresh && (
            <button type="button" onClick={onRefresh} className="p-1.5 rounded-md text-slate-400 hover:text-white" title="Refresh preview" data-testid="preview-refresh">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          )}
          {openUrl && (
            <a href={openUrl} target="_blank" rel="noreferrer" className="p-1.5 rounded-md text-slate-400 hover:text-white" title="Open in new tab" data-testid="preview-open">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>

      <div ref={paneRef} className="flex-1 overflow-y-auto overflow-x-hidden bg-slate-950 relative">
        {/* Container sized to scaled dimensions so scrollbar reflects scaled height */}
        <div style={{ width: paneWidth, height: scaledHeight, position: "relative" }}>
          {/* Content rendered at native width, then scaled */}
          <div
            ref={childRef}
            style={{
              width: cfg.width,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivePreviewPane;
